import React, { useState } from 'react';
import { ArrowRight, Loader2, ChevronDown } from 'lucide-react';
import { toast } from './ui/sonner';
import Reveal from './Reveal';
import { SectionMarker } from './Gap';
import SiteFooter from './SiteFooter';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const reasons = ['Project Inquiry', 'Careers', 'General'];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', reason: '', message: '' });
  const [resume, setResume] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onResume = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file.');
      e.target.value = '';
      return;
    }
    setResume(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please add your name and email.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('bad response');
      toast.success('Message sent. We will be in touch soon.');
      setForm({ name: '', email: '', reason: '', message: '' });
      setResume(null);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    'w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-3 font-mono  text-[var(--muted)] placeholder:text-[var(--muted-2)]/50 transition-colors';

  return (
    <section id="contact" className="relative section-pad-t border-t border-[var(--border)] bg-[var(--bg-2)]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        {/* Belief, closing statement on an engineered brand-blue panel */}
        <div className="mb-[var(--section-pad)] rounded-2xl px-5 sm:px-8 py-16 md:py-20 text-left md:text-center relative overflow-hidden border border-[rgba(255,255,255,0.12)]" style={{ backgroundColor: 'var(--brand-blue)' }}>
          {/* blueprint grid */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.16]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          {/* wider accent guide lines */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.10]" style={{ backgroundImage: 'linear-gradient(var(--accent) 1px, transparent 1px), linear-gradient(90deg, var(--accent) 1px, transparent 1px)', backgroundSize: '200px 200px' }} />
          {/* corner registration brackets */}
          <span className="pointer-events-none absolute top-5 left-5 w-6 h-6 border-l-2 border-t-2 border-[var(--accent)]/70" />
          <span className="pointer-events-none absolute top-5 right-5 w-6 h-6 border-r-2 border-t-2 border-[var(--accent)]/70" />
          <span className="pointer-events-none absolute bottom-5 left-5 w-6 h-6 border-l-2 border-b-2 border-[var(--accent)]/70" />
          <span className="pointer-events-none absolute bottom-5 right-5 w-6 h-6 border-r-2 border-b-2 border-[var(--accent)]/70" />
          {/* measurement ticks along the top */}
          <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 flex items-start gap-[10px] opacity-50">
            {Array.from({ length: 21 }).map((_, i) => (
              <span key={i} className="w-px bg-white" style={{ height: i % 5 === 0 ? '12px' : '6px' }} />
            ))}
          </div>
          <Reveal><p className="label mb-8 !text-[rgba(255,255,255,0.6)] relative">What we believe</p></Reveal>
          <Reveal delay={100}>
            <h3 className="font-display font-medium text-[clamp(2.4rem,6vw,5.2rem)] leading-[1.02] tracking-[-0.02em] text-white relative">
              Simplicity.<br />
              <span className="text-[var(--accent)] font-normal">Engineered from Complexity.</span>
            </h3>
          </Reveal>
        </div>

        <Reveal>
          <p className="font-serif-c italic text-[var(--accent)] text-[1.6rem] md:text-[2rem] text-left md:text-center mb-[var(--section-pad)]">The rest happens in person.</p>
        </Reveal>

        <Reveal><SectionMarker index="X" label="WALK IN. CARRY SOMETHING OUT." className="hidden label-gap" /></Reveal>
        <Reveal><p className="hidden label label-gap">The best experiences do not end when people leave the room</p></Reveal>

        <Reveal>
          <h2 className="hidden font-display font-medium text-[clamp(2.2rem,5.2vw,5rem)] leading-[1.02] tracking-[-0.02em] max-w-[20ch]">
            When people should walk in <span className="font-serif-c italic text-[var(--accent)] font-normal">and walk out carrying something they can’t quite put down</span>, the first conversation is with us.
          </h2>
        </Reveal>

        <Reveal>
          <span id="start-conversation" className="font-display font-medium text-[clamp(2.4rem,7.4vw,7rem)] leading-[0.98] tracking-[-0.02em] block w-full text-left md:text-center whitespace-normal md:whitespace-nowrap mb-10 cursor-default transition-colors duration-300 hover:text-[var(--accent)] scroll-mt-28">Start a conversation</span>
        </Reveal>

        {/* Contact block */}
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-12">
            <Reveal delay={60}>
              <p className="hidden text-[var(--muted)]  leading-relaxed font-light max-w-[52ch] mb-10">
                They leave with a memory. A feeling. A conversation. Something they noticed. Something they understood differently. Something they cannot quite put down. That is what we want the spaces we create to do. If that is what you are trying to build, start the conversation with us.
              </p>
            </Reveal>

            {/* Inquiry form */}
            <Reveal delay={80}>
              <form onSubmit={submit} data-testid="contact-form" className="relative border border-[var(--border)] rounded-2xl p-6 md:p-8">
                {/* corner registration brackets */}
                <span className="pointer-events-none absolute top-3 left-3 w-4 h-4 border-l border-t border-[var(--accent)]/50" />
                <span className="pointer-events-none absolute top-3 right-3 w-4 h-4 border-r border-t border-[var(--accent)]/50" />
                <span className="pointer-events-none absolute bottom-3 left-3 w-4 h-4 border-l border-b border-[var(--accent)]/50" />
                <span className="pointer-events-none absolute bottom-3 right-3 w-4 h-4 border-r border-b border-[var(--accent)]/50" />

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label  block mb-1">Name</label>
                    <input data-testid="contact-name" className={fieldClass} value={form.name} onChange={set('name')} placeholder="Your Name" />
                  </div>
                  <div>
                    <label className="label  block mb-1">Email</label>
                    <input data-testid="contact-email" type="email" className={fieldClass} value={form.email} onChange={set('email')} placeholder="Your Email Address" />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="label  block mb-1">Purpose</label>
                  <div className="relative">
                    <select
                      data-testid="contact-reason"
                      className={`${fieldClass} appearance-none cursor-pointer pr-8 ${form.reason === '' ? 'text-[var(--muted-2)]/50' : ''}`}
                      value={form.reason}
                      onChange={(e) => {
                        const reason = e.target.value;
                        setForm((f) => ({ ...f, reason }));
                        if (reason !== 'Careers') setResume(null);
                      }}
                    >
                      <option value="" disabled hidden>Select A Purpose</option>
                      {reasons.map((r) => (
                        <option key={r} value={r} className="bg-[var(--bg-2)] text-[var(--text)]">{r}</option>
                      ))}
                    </select>
                    <ChevronDown size={18} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="label  block mb-1">Message</label>
                  <textarea data-testid="contact-message" rows={4} maxLength={300} className={`${fieldClass} resize-none overflow-x-hidden`} value={form.message} onChange={set('message')} placeholder={form.reason === 'Careers' ? 'Tell Us About The Role You Are After And Share A Link To Your Portfolio Or Résumé.' : 'Tell Us What You Are Building.'} />
                  <div className="mt-1 text-left md:text-right"><span className="label !text-[var(--muted-2)]">{form.message.length}/300</span></div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={sending}
                  className="mt-8 group inline-flex items-center gap-3 disabled:opacity-60"
                >
                  <span className="label !text-[var(--accent)] group-hover:brightness-110 transition-colors">{sending ? 'Sending' : 'Send message'}</span>
                  <span className="circle-btn w-9 h-9 rounded-full border border-[var(--accent)] flex items-center justify-center group-hover:bg-[var(--accent)] transition-colors duration-300">
                    {sending
                      ? <Loader2 size={15} className="animate-spin text-[var(--accent)]" />
                      : <ArrowRight size={15} className="arrow text-[var(--accent)] group-hover:text-[#0A0D17] transition-colors" />}
                  </span>
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Site footer (shared with legal pages) */}
      <div className="mt-16 md:mt-40">
        <SiteFooter />
      </div>
    </section>
  );
};

export default Contact;
